import { useCallback, useMemo, useState } from 'react';
import { Button, ButtonSizes, ButtonVariants } from '@carboledger/ui';
import { FileUp, Plus } from '@carboledger/ui/icons';
import { PageContainer, PageHeader } from '@/components/layout';
import { useTranslations } from '@/hooks';
import { SuppliersTable } from '../components/SuppliersTable';
import { SupplierModal } from '../components/SupplierModal';
import { useGetSupplierFormQuery } from '../api/suppliersApi';
import styles from './SuppliersPage.module.scss';

export const SuppliersPage = () => {
  const t = useTranslations();

  const [editTarget, setEditTarget] = useState(null);
  const [hasSuppliers, setHasSuppliers] = useState(false);

  const { currentData: supplier } = useGetSupplierFormQuery(editTarget?.id, {
    skip: !editTarget?.id,
  });

  const handleAdd = useCallback(() => setEditTarget({}), []);
  const handleEdit = useCallback((row) => setEditTarget({ id: row.id }), []);
  const handleCloseModal = useCallback(() => setEditTarget(null), []);

  const handleTotalChange = useCallback((total) => setHasSuppliers(total > 0), []);

  const addIcon = useMemo(() => <Plus />, []);
  const importIcon = useMemo(() => <FileUp />, []);

  const headerActions = useMemo(
    () => (
      <>
        {hasSuppliers && (
          <Button
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
            leftIcon={importIcon}
            disabled
          >
            {t('SUPPLIERS_IMPORT_SITES')}
          </Button>
        )}
        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          leftIcon={importIcon}
          disabled
        >
          {t('SUPPLIERS_IMPORT')}
        </Button>
        <Button
          variant={ButtonVariants.primary}
          size={ButtonSizes.sm}
          leftIcon={addIcon}
          onClick={handleAdd}
        >
          {t('SUPPLIERS_ADD')}
        </Button>
      </>
    ),
    [t, hasSuppliers, importIcon, addIcon, handleAdd],
  );

  const isModalReady = editTarget !== null && (!editTarget.id || Boolean(supplier));

  return (
    <PageContainer className={styles.page}>
      <PageHeader title={t('SUPPLIERS_TITLE')} actions={headerActions} />

      <SuppliersTable onAdd={handleAdd} onEdit={handleEdit} onTotalChange={handleTotalChange} />

      <SupplierModal open={isModalReady} onClose={handleCloseModal} supplier={supplier} />
    </PageContainer>
  );
};
